<template>
  <!-- ── Desktop: icon rail that expands on hover (≥1200px) ─────────── -->
  <aside
    ref="railEl"
    class="ui-rail"
    :class="{ expanded: railExpanded }"
    :aria-label="$t('sidebar.mainNav')"
    @mouseenter="expandRail"
    @mouseleave="collapseRail"
    @focusin="expandRail"
    @focusout="onRailFocusOut"
  >
    <div class="ui-rail-panel">
      <router-link to="/" class="ui-rail-head" :aria-label="$t('header.home')">
        <span class="ui-rail-slot">
          <span class="ui-rail-brand"><img :src="logoSrc" alt="" /></span>
        </span>
        <span class="ui-rail-label ui-rail-title">{{ $t("sidebar.mainMenu") }}</span>
      </router-link>
      <span class="ui-rail-sep" aria-hidden="true"></span>

      <nav class="ui-rail-nav">
        <template v-for="(item, index) in visibleMenu" :key="item.key">
          <span
            v-if="item.separatorBefore && index > 0"
            class="ui-rail-sep ui-rail-sep-sm"
            aria-hidden="true"
          ></span>

          <template v-if="item.children">
            <button
              type="button"
              class="ui-rail-link"
              :class="{ active: isItemActive(item), open: railExpanded && railGroup === item.key }"
              :aria-label="$t(item.label)"
              :aria-expanded="railExpanded && railGroup === item.key"
              @click="toggleRailGroup(item)"
            >
              <span class="ui-rail-slot">
                <span class="ui-rail-bubble">
                  <i :class="item.icon" aria-hidden="true"></i>
                  <span class="ui-rail-caret" aria-hidden="true"></span>
                </span>
              </span>
              <span class="ui-rail-label">{{ $t(item.label) }}</span>
              <i class="ph ph-caret-down ui-rail-chevron" aria-hidden="true"></i>
            </button>
            <div v-show="railExpanded && railGroup === item.key" class="ui-rail-sub">
              <router-link
                v-for="child in item.children"
                :key="child.to"
                :to="child.to"
                class="ui-rail-sublink"
                :class="{ active: isPathActive(child.to) }"
                :aria-current="isPathActive(child.to) ? 'page' : undefined"
              >
                <i :class="child.icon" aria-hidden="true"></i>
                <span>{{ $t(child.label) }}</span>
              </router-link>
            </div>
          </template>

          <router-link
            v-else
            :to="item.to"
            class="ui-rail-link"
            :class="{ active: isItemActive(item) }"
            :aria-label="$t(item.label)"
            :aria-current="isItemActive(item) ? 'page' : undefined"
          >
            <span class="ui-rail-slot">
              <span class="ui-rail-bubble"><i :class="item.icon" aria-hidden="true"></i></span>
            </span>
            <span class="ui-rail-label">{{ $t(item.label) }}</span>
          </router-link>
        </template>
      </nav>
    </div>
  </aside>

  <!-- ── Tablet / mobile: slide-in drawer (<1200px) ────────────────── -->
  <div
    class="ui-drawer-backdrop"
    :class="{ show: stateStoreInstance.open }"
    aria-hidden="true"
    @click="handleRouteClick"
  ></div>
  <aside
    class="ui-drawer"
    :class="{ open: stateStoreInstance.open }"
    :aria-label="$t('sidebar.mainNav')"
    :aria-hidden="!stateStoreInstance.open"
  >
    <div class="ui-drawer-head">
      <router-link to="/" class="ui-drawer-brand" :aria-label="$t('header.home')">
        <img :src="logoSrc" alt="" />
      </router-link>
      <button
        type="button"
        class="ui-icon-btn"
        :aria-label="$t('sidebar.closeMenu')"
        @click="handleRouteClick"
      >
        <i class="ph ph-x" aria-hidden="true"></i>
      </button>
    </div>

    <nav class="ui-drawer-nav">
      <template v-for="item in visibleMenu" :key="item.key">
        <template v-if="item.children">
          <button
            type="button"
            class="ui-drawer-link"
            :class="{ active: isItemActive(item) }"
            :aria-expanded="drawerGroup === item.key"
            @click="drawerGroup = drawerGroup === item.key ? null : item.key"
          >
            <span class="ui-drawer-icon"><i :class="item.icon" aria-hidden="true"></i></span>
            <span class="ui-drawer-label">{{ $t(item.label) }}</span>
            <i
              class="ph ph-caret-down ui-drawer-caret"
              :class="{ rotated: drawerGroup === item.key }"
              aria-hidden="true"
            ></i>
          </button>
          <div v-show="drawerGroup === item.key" class="ui-drawer-sub">
            <router-link
              v-for="child in item.children"
              :key="child.to"
              :to="child.to"
              class="ui-drawer-sublink"
              :class="{ active: isPathActive(child.to) }"
            >
              {{ $t(child.label) }}
            </router-link>
          </div>
        </template>

        <router-link
          v-else
          :to="item.to"
          class="ui-drawer-link"
          :class="{ active: isItemActive(item) }"
          :aria-current="isItemActive(item) ? 'page' : undefined"
        >
          <span class="ui-drawer-icon"><i :class="item.icon" aria-hidden="true"></i></span>
          <span class="ui-drawer-label">{{ $t(item.label) }}</span>
        </router-link>
      </template>
    </nav>
  </aside>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import stateStore from "../../utils/store";
import { useAuthStore } from "@/stores/auth";
import { useSettingsStore } from "@/stores/settings";
import { canAccessPath } from "@/router";

interface MenuChild {
  label: string;
  to: string;
  icon: string;
  // Extra sidebar rule: the user needs all of these. The route's own
  // meta.permissions are always checked as well (see canAccessPath).
  permissions?: string[];
}

interface MenuItem {
  key: string;
  label: string;
  icon: string;
  // A section is either a single link (`to`) or a group (`children`).
  // A group is hidden when none of its children is visible.
  to?: string;
  permissions?: string[];
  children?: MenuChild[];
  // extra route prefixes that should highlight this entry (edit/detail pages)
  match?: string[];
  separatorBefore?: boolean;
}

// Same sections, routes and permissions as before the redesign.
// Labels are translation keys (src/i18n/locales).
const MENU: MenuItem[] = [
  { key: "Home", label: "menu.dashboard", icon: "ph ph-house", to: "/", permissions: ["HOME_PAGE"] },
  { key: "Tracking", label: "menu.trackingDrivers", icon: "ph ph-navigation-arrow", to: "/tracking-drivers", permissions: ["TRACKING_DRIVERS"], match: ["/tracking-drivers"] },
  {
    key: "Colis",
    label: "menu.parcels",
    icon: "ph ph-package",
    separatorBefore: true,
    children: [
      { label: "menu.parcelsList", to: "/colis-list", icon: "ph ph-list-bullets", permissions: ["PARCELS"] },
      { label: "menu.newParcels", to: "/new-colis-list", icon: "ph ph-sparkle", permissions: ["PARCELS"] },
      { label: "menu.addParcel", to: "/add-colis", icon: "ph ph-plus-circle", permissions: ["PARCELS", "ADD_PARCEL"] },
    ],
  },
  {
    key: "Stock",
    label: "menu.inventory",
    icon: "ph ph-warehouse",
    children: [
      { label: "menu.productsList", to: "/products-list", icon: "ph ph-tag", permissions: ["STOCK"] },
      { label: "menu.stockParcels", to: "/colis-stock-list", icon: "ph ph-stack", permissions: ["STOCK", "PARCELS"] },
    ],
  },
  {
    key: "Bl",
    label: "menu.deliveryNotes",
    icon: "ph ph-file-text",
    children: [
      { label: "menu.deliveryNotes", to: "/delivery-notes", icon: "ph ph-files", permissions: ["BL"] },
      { label: "menu.deliveryStockNotes", to: "/delivery-stock-notes", icon: "ph ph-stack", permissions: ["BL_S"] },
      { label: "menu.packingStation", to: "/packing", icon: "ph ph-package", permissions: ["PACKING"] },
    ],
  },
  { key: "Reception", label: "menu.parcelReception", icon: "ph ph-clipboard-text", to: "/reception-notes", permissions: ["RECEPTION"] },
  { key: "Sent", label: "menu.sentNotes", icon: "ph ph-paper-plane-tilt", to: "/sent-notes", permissions: ["BE"] },
  { key: "Distribution", label: "menu.distributionNotes", icon: "ph ph-cube", to: "/distribution-notes", permissions: ["BDIS"] },
  // { key: "Pointage", label: "menu.driverCheck", icon: "ph ph-check", to: "/pointage-notes", permissions: ["SCORING"] },

  {
    key: "Payments",
    label: "menu.driverInvoices",
    icon: "ph ph-moped",
    separatorBefore: true,
    children: [
      { label: "menu.driverInvoices", to: "/payment-note-liste", icon: "ph ph-receipt", permissions: ["BP_L"] },
      { label: "menu.zoneInvoices", to: "/payment-zone-liste", icon: "ph ph-map-pin", permissions: ["BP_Z"] },
    ],
  },
  {
    key: "Return",
    label: "menu.returnNotes",
    icon: "ph ph-arrow-counter-clockwise",
    children: [
      { label: "menu.zoneReturns", to: "/return-zone-liste", icon: "ph ph-map-pin", permissions: ["BR_Z"] },
      { label: "menu.clientReturns", to: "/return-client-liste", icon: "ph ph-user", permissions: ["BR_C"] },
    ],
  },
  {
    key: "invoices",
    label: "menu.clientInvoices",
    icon: "ph ph-money",
    children: [
      { label: "menu.invoicesList", to: "/invoices-liste", icon: "ph ph-list-bullets", permissions: ["INV"] },
      { label: "menu.newInvoice", to: "/add-invoices", icon: "ph ph-plus-circle", permissions: ["INV"] },
      { label: "menu.transfers", to: "/virements-liste", icon: "ph ph-bank", permissions: ["INV", "VIREMENT"] },
    ],
  },
  { key: "Refunds", label: "menu.refunds", icon: "ph ph-hand-coins", to: "/refund-requests", permissions: ["REFUND_REQUESTS"], match: ["/refund-requests"] },
  {
    key: "Custs",
    label: "menu.customers",
    icon: "ph ph-users-three",
    separatorBefore: true,
    children: [
      { label: "menu.customersList", to: "/customers-list", icon: "ph ph-list-bullets", permissions: ["CUSTOMERS"] },
      { label: "menu.newCustomers", to: "/new-customers-list", icon: "ph ph-user-plus", permissions: ["NEW_CUSTOMERS"] },
      { label: "menu.verificationCenter", to: "/verifications", icon: "ph ph-shield-check", permissions: ["CUSTOMERS"] },
      { label: "menu.ribRequests", to: "/rib-requests-list", icon: "ph ph-bank", permissions: ["REQUEST_RIB"] },
      { label: "menu.phoneRequests", to: "/phone-requests-list", icon: "ph ph-phone", permissions: ["REQUEST_PHONE"] },
    ],
  },
  { key: "Claims", label: "menu.claims", icon: "ph ph-warning-circle", to: "/claims-list", permissions: ["CLAIM"] },
  { key: "Exchanges", label: "menu.exchanges", icon: "ph ph-arrows-left-right", to: "/exchanges", permissions: ["EXCHANGES"], match: ["/exchanges"] },
  { key: "ReturnDeadlines", label: "menu.driverReturnsFollowup", icon: "ph ph-timer", to: "/return-deadlines", permissions: ["RETURN_DEADLINES"], match: ["/return-deadlines"] },
  // { key: "ParcelFollowups", label: "menu.parcelFollowups", icon: "ph ph-binoculars", to: "/parcel-followups", permissions: ["PARCEL_FOLLOWUPS"], match: ["/parcel-followups"] },
  {
    key: "Pickup",
    label: "menu.pickup",
    icon: "ph ph-truck",
    children: [
      { label: "menu.pickupNotes", to: "/pickup-list", icon: "ph ph-clipboard-text", permissions: ["PICK_UP"] },
      { label: "menu.pickupRequests", to: "/requests-pickup-list", icon: "ph ph-hand-coins", permissions: ["PICK_UP"] },
    ],
  },
  {
    key: "Eleven",
    label: "menu.users",
    icon: "ph ph-user-gear",
    children: [
      { label: "menu.usersList", to: "/users-list", icon: "ph ph-list-bullets", permissions: ["USERS"] },
      { label: "menu.newDrivers", to: "/new-driver-list", icon: "ph ph-user-plus", permissions: ["NEW_LIVREUR"] },
    ],
  },
  { key: "DriversStatus", label: "menu.driversStatus", icon: "ph ph-clipboard-text", to: "/driver-returns-report", permissions: ["USERS"] },
  { key: "Spents", label: "menu.expenses", icon: "ph ph-wallet", to: "/spents", permissions: ["SETTINGS"] },
  {
    key: "Transport",
    label: "menu.transport",
    icon: "ph ph-truck",
    children: [
      { label: "menu.transportsList", to: "/transports", icon: "ph ph-list-bullets", permissions: ["SETTINGS"] },
      { label: "menu.carrierInvoices", to: "/transport-invoices", icon: "ph ph-receipt", permissions: ["TRANSPORT_INVOICES"] },
    ],
  },

  {
    key: "TwentyTwo",
    label: "menu.settings",
    icon: "ph ph-gear",
    separatorBefore: true,
    children: [
      { label: "menu.profile", to: "/profile", icon: "ph ph-user-circle" },
      { label: "menu.general", to: "/account-settings", icon: "ph ph-sliders", permissions: ["SETTINGS"] },
      { label: "menu.zones", to: "/zones-settings", icon: "ph ph-map-trifold", permissions: ["SETTINGS"] },
      { label: "menu.pricePacks", to: "/packs-settings", icon: "ph ph-package", permissions: ["SETTINGS"] },
      { label: "menu.cities", to: "/cities-settings", icon: "ph ph-buildings", permissions: ["SETTINGS"] },
      { label: "menu.agencies", to: "/agencies-settings", icon: "ph ph-storefront", permissions: ["SETTINGS"] },
      { label: "menu.whatsapp", to: "/whatsapp/dashboard", icon: "ph ph-whatsapp-logo", permissions: ["WHATSAPP"] },
      { label: "menu.ads", to: "/ads-settings", icon: "ph ph-megaphone", permissions: ["SETTINGS_ADS"] },
      { label: "menu.blackList", to: "/black-list", icon: "ph ph-prohibit", permissions: ["BLACK_LISTE"] },
    ],
  },
];

export default defineComponent({
  name: "MainSidebar",
  setup() {
    const stateStoreInstance = stateStore;
    const auth = useAuthStore();
    // Settings are fetched by App.vue when the layout is shown.
    const settingsStore = useSettingsStore();
    const route = useRoute();

    function isVisible(item: { to?: string; permissions?: string[] }) {
      if (item.permissions && !auth.canAll(item.permissions)) return false;
      return item.to ? canAccessPath(item.to) : true;
    }

    function handleRouteClick() {
      if (stateStoreInstance.open) {
        stateStoreInstance.onChange();
      }
    }

    // Recomputed only when the permission list changes.
    const visibleMenu = computed<MenuItem[]>(() =>
      MENU.map((item) => (item.children ? { ...item, children: item.children.filter(isVisible) } : item)).filter(
        (item) => (item.children ? item.children.length > 0 : isVisible(item))
      )
    );

    const logoSrc = computed(() => settingsStore.logo || require("../../assets/images/logo.png"));

    // ── Active state ────────────────────────────────────────────────
    function isPathActive(path: string): boolean {
      if (path === "/") return route.path === "/";
      return route.path === path || route.path.startsWith(path + "/");
    }
    function isItemActive(item: MenuItem): boolean {
      if (item.to && isPathActive(item.to)) return true;
      if (item.children?.some((c) => isPathActive(c.to))) return true;
      return !!item.match?.some((p) => isPathActive(p));
    }

    function activeGroupKey(): string | null {
      const group = visibleMenu.value.find((i) => i.children && isItemActive(i));
      return group ? group.key : null;
    }

    // ── Rail (desktop): expands with labels while hovered/focused ───
    const railEl = ref<HTMLElement | null>(null);
    const railExpanded = ref(false);
    // Group opened inline in the expanded rail (defaults to the active one)
    const railGroup = ref<string | null>(null);
    let railTimer: ReturnType<typeof setTimeout> | undefined;

    function expandRail() {
      clearTimeout(railTimer);
      // small delay so merely crossing the rail with the mouse doesn't open it
      railTimer = setTimeout(() => (railExpanded.value = true), 60);
    }
    function collapseRail() {
      clearTimeout(railTimer);
      railTimer = setTimeout(() => {
        railExpanded.value = false;
        railGroup.value = activeGroupKey();
      }, 140);
    }
    function onRailFocusOut(event: FocusEvent) {
      const next = event.relatedTarget as Node | null;
      if (!next || !railEl.value?.contains(next)) collapseRail();
    }
    function toggleRailGroup(item: MenuItem) {
      clearTimeout(railTimer);
      railExpanded.value = true;
      railGroup.value = railGroup.value === item.key ? null : item.key;
    }
    function onDocumentClick(event: MouseEvent) {
      // touch screens: tapping outside the rail closes it
      if (railExpanded.value && railEl.value && !railEl.value.contains(event.target as Node)) collapseRail();
    }
    function onKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        collapseRail();
        handleRouteClick();
      }
    }

    // ── Drawer (mobile groups) ──────────────────────────────────────
    const drawerGroup = ref<string | null>(null);

    watch(
      () => route.path,
      () => {
        handleRouteClick();
        drawerGroup.value = activeGroupKey();
        railGroup.value = activeGroupKey();
      },
      { immediate: true }
    );

    // Push the page content aside while the rail is open (see _layout.scss)
    watch(railExpanded, (open) => document.body.classList.toggle("rail-expanded", open));

    onMounted(() => {
      document.addEventListener("click", onDocumentClick);
      document.addEventListener("keydown", onKeydown);
    });
    onBeforeUnmount(() => {
      clearTimeout(railTimer);
      document.body.classList.remove("rail-expanded");
      document.removeEventListener("click", onDocumentClick);
      document.removeEventListener("keydown", onKeydown);
    });

    return {
      stateStoreInstance,
      handleRouteClick,
      settingsStore,
      visibleMenu,
      logoSrc,
      isPathActive,
      isItemActive,
      railEl,
      railExpanded,
      railGroup,
      expandRail,
      collapseRail,
      onRailFocusOut,
      toggleRailGroup,
      drawerGroup,
    };
  },
});
</script>

<style scoped>
/* Layout sizes come from tokens: --ui-frame-gap, --ui-rail-width. */

/* ── Rail ─────────────────────────────────────────────────────────── */
/* Collapsed: icons only. Hovered/focused: slides open and pushes the page
   content aside (body.rail-expanded) and shows each section's name. */
.ui-rail {
  --rail-open-width: 272px;
  position: fixed;
  z-index: 1030;
  top: var(--ui-frame-gap);
  bottom: var(--ui-frame-gap);
  left: var(--ui-frame-gap);
  width: var(--ui-rail-width);
  display: none;
  transition: width 280ms var(--ui-ease-spring);
}
.ui-rail.expanded {
  width: var(--rail-open-width);
}
.ui-rail-panel {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 14px 0;
  overflow: hidden;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-2xl);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-sidebar);
  transition: box-shadow var(--ui-dur) var(--ui-ease);
}
.ui-rail.expanded .ui-rail-panel {
  box-shadow: var(--ui-shadow-pop), 0 24px 60px rgba(28, 25, 23, 0.18);
}

/* Every row = fixed icon column (same width as the collapsed rail) + label */
.ui-rail-slot {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: calc(var(--ui-rail-width) - 2px);
}
.ui-rail-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0;
  transform: translateX(-6px);
  transition: opacity 160ms var(--ui-ease), transform 220ms var(--ui-ease);
}
.ui-rail.expanded .ui-rail-label {
  opacity: 1;
  transform: none;
  transition-delay: 70ms;
}

.ui-rail-head {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  color: var(--ui-text);
  text-decoration: none;
}
.ui-rail-brand {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  box-shadow: 0 8px 20px rgba(var(--ui-accent-rgb), 0.14);
  overflow: hidden;
  transition: transform var(--ui-dur) var(--ui-ease-spring);
}
.ui-rail-head:hover .ui-rail-brand {
  transform: scale(1.05);
}
.ui-rail-brand img {
  width: 82%;
  height: auto;
  object-fit: contain;
}
.ui-rail-title {
  padding-right: 16px;
  font-size: var(--ui-fs-base);
  font-weight: var(--ui-fw-black);
}

.ui-rail-sep {
  display: block;
  flex: 0 0 auto;
  width: 36px;
  height: 2px;
  margin: 14px 0 10px calc((var(--ui-rail-width) - 38px) / 2);
  border-radius: 2px;
  background: var(--ui-border);
}
.ui-rail-sep-sm {
  width: 24px;
  margin: 4px 0 4px calc((var(--ui-rail-width) - 26px) / 2);
}

.ui-rail-nav {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0 8px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-width: none;
}
.ui-rail-nav::-webkit-scrollbar {
  display: none;
}

.ui-rail-link {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 54px;
  padding: 0 12px 0 0;
  border: 0;
  background: transparent;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}
.ui-rail-bubble {
  position: relative;
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border: 1px solid var(--ui-border);
  border-radius: 50%;
  background: var(--ui-surface-sunken);
  color: var(--ui-text-secondary);
  font-size: 21px;
  transition: transform 320ms var(--ui-ease-spring), color var(--ui-dur) var(--ui-ease),
    background-color var(--ui-dur) var(--ui-ease), border-color var(--ui-dur) var(--ui-ease),
    box-shadow var(--ui-dur) var(--ui-ease);
}
.ui-rail-bubble i {
  line-height: 1;
}
.ui-rail-link:hover,
.ui-rail-link:focus-visible,
.ui-rail-link.open {
  color: var(--ui-accent-text);
}
.ui-rail-link:hover .ui-rail-bubble,
.ui-rail-link:focus-visible .ui-rail-bubble,
.ui-rail-link.open .ui-rail-bubble {
  color: var(--ui-accent-text);
  border-color: var(--ui-accent);
  background: var(--ui-accent-soft);
  transform: scale(1.06);
}
.ui-rail-link:focus-visible {
  outline: none;
}

/* Active section: white bubble with an accent halo, label in accent */
.ui-rail-link.active {
  color: var(--ui-accent-text);
  font-weight: var(--ui-fw-black);
}
.ui-rail-link.active .ui-rail-bubble {
  width: 50px;
  height: 50px;
  color: var(--ui-accent-text);
  background: var(--ui-surface);
  border-color: rgba(var(--ui-accent-rgb), 0.45);
  box-shadow: 0 0 0 3px var(--ui-surface), 0 0 0 5px rgba(var(--ui-accent-rgb), 0.22),
    0 10px 24px rgba(var(--ui-accent-rgb), 0.18);
  font-size: 22px;
}

/* Dot marking a section that has sub-pages */
.ui-rail-caret {
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ui-accent);
  box-shadow: 0 0 0 2px var(--ui-surface);
}
.ui-rail-chevron {
  flex: 0 0 auto;
  margin-left: 6px;
  font-size: 13px;
  opacity: 0;
  transition: opacity 160ms var(--ui-ease), transform var(--ui-dur) var(--ui-ease);
}
.ui-rail.expanded .ui-rail-chevron {
  opacity: 0.7;
}
.ui-rail-link.open .ui-rail-chevron {
  transform: rotate(180deg);
}

/* Sub-pages, opened inline under their section */
.ui-rail-sub {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: -2px 14px 6px calc(var(--ui-rail-width) / 2 - 1px);
  padding-left: 14px;
  border-left: 2px solid var(--ui-border);
  animation: ui-rail-sub-in var(--ui-dur) var(--ui-ease);
}
.ui-rail-sublink {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 7px 10px;
  border-radius: var(--ui-radius-sm);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  white-space: nowrap;
  text-decoration: none;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease);
}
.ui-rail-sublink span {
  overflow: hidden;
  text-overflow: ellipsis;
}
.ui-rail-sublink i {
  flex: 0 0 auto;
  color: var(--ui-text-muted);
  font-size: 16px;
}
.ui-rail-sublink:hover {
  background: var(--ui-surface-sunken);
  color: var(--ui-accent-text);
}
.ui-rail-sublink.active {
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
  font-weight: var(--ui-fw-heavy);
}
.ui-rail-sublink:hover i,
.ui-rail-sublink.active i {
  color: var(--ui-accent-text);
}

@keyframes ui-rail-sub-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .ui-rail,
  .ui-rail-label,
  .ui-rail-bubble,
  .ui-rail-sub {
    transition: none;
    animation: none;
  }
}

/* ── Drawer ───────────────────────────────────────────────────────── */
.ui-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1040;
  background: rgba(28, 25, 23, 0.32);
  backdrop-filter: blur(3px);
  opacity: 0;
  visibility: hidden;
  transition: opacity var(--ui-dur) var(--ui-ease), visibility var(--ui-dur);
}
.ui-drawer-backdrop.show {
  opacity: 1;
  visibility: visible;
}
.ui-drawer {
  position: fixed;
  z-index: 1045;
  top: 12px;
  bottom: 12px;
  left: 12px;
  width: min(300px, calc(100vw - 48px));
  display: flex;
  flex-direction: column;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-xl);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-pop);
  transform: translateX(calc(-100% - 24px));
  visibility: hidden;
  transition: transform var(--ui-dur-slow) var(--ui-ease-spring), visibility var(--ui-dur-slow);
}
.ui-drawer.open {
  transform: translateX(0);
  visibility: visible;
}
.ui-drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 12px 20px;
  border-bottom: 1px solid var(--ui-border);
}
.ui-drawer-brand img {
  height: 44px;
  width: auto;
  object-fit: contain;
}
.ui-drawer-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
}
.ui-drawer-link {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 6px 12px 6px 6px;
  border: 0;
  border-radius: var(--ui-radius-md);
  background: transparent;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-base);
  font-weight: var(--ui-fw-bold);
  text-align: left;
  text-decoration: none;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease);
}
.ui-drawer-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--ui-surface-sunken);
  font-size: 18px;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease);
}
.ui-drawer-label {
  flex: 1 1 auto;
}
.ui-drawer-caret {
  font-size: 14px;
  transition: transform var(--ui-dur) var(--ui-ease);
}
.ui-drawer-caret.rotated {
  transform: rotate(180deg);
}
.ui-drawer-link:hover {
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
}
.ui-drawer-link.active {
  color: var(--ui-accent-text);
  font-weight: var(--ui-fw-black);
}
.ui-drawer-link.active .ui-drawer-icon {
  background: var(--ui-accent);
  color: #FFFFFF;
  box-shadow: 0 6px 14px rgba(var(--ui-accent-rgb), 0.3);
}
.ui-drawer-sub {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 2px 0 6px 23px;
  padding-left: 18px;
  border-left: 2px solid var(--ui-border);
}
.ui-drawer-sublink {
  padding: 9px 12px;
  border-radius: var(--ui-radius-sm);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  text-decoration: none;
}
.ui-drawer-sublink:hover {
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
}
.ui-drawer-sublink.active {
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
  font-weight: var(--ui-fw-heavy);
}

/* Desktop: rail on, drawer off */
@media (min-width: 1200px) {
  .ui-rail {
    display: block;
  }
  .ui-drawer,
  .ui-drawer-backdrop {
    display: none;
  }
}
</style>
